import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useDeferredLoading } from 'src/hooks/useDeferredLoading';
import ItemDetailsModal from './components/ItemDetailsModal';
import ListItemRenderer from './components/ListItemRenderer';
import Pagination from '../Pagination';
import { ReactComponent as CaretUpIcon } from 'src/assets/images/CaretUp.svg';
import { ReactComponent as CaretDownIcon } from 'src/assets/images/CaretDown.svg';

import { StyledTableScroll } from '../styled';
import { StyledHeaderCell, SortIcon, StyledTable, TableBodyWrap } from './styled';

const getRowKey = (item: { [key: string]: unknown }, index: number) => {
  const candidate = item.id ?? item._id ?? item.wallet_id ?? item.kyc_id ?? item.user_id;
  return candidate != null && candidate !== '' ? String(candidate) : `row-${index}`;
};

interface ITable {
  headers: string[];
  items: { [key: string]: any }[];
  fieldsToShow: string[];
  columns: string;
  currentPage: number;
  totalPages: number;
  currencies?: any;
  detailsModalTitle?: string;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  // eslint-disable-next-line no-unused-vars
  actions?: (item: { [key: string]: any }) => React.ReactNode;
  actionsHeader?: string;
  /** When provided, replaces the default ItemDetailsModal for row clicks. */
  // eslint-disable-next-line no-unused-vars
  renderDetailsModal?: (item: { [key: string]: any }, onClose: () => void) => React.ReactNode;
  /** Custom cell renderer; return null/undefined to fall back to ListItemRenderer. */
  // eslint-disable-next-line no-unused-vars
  renderCell?: (item: { [key: string]: any }, field: string) => React.ReactNode | null | undefined;
  /** Called instead of opening the default details modal when provided. */
  // eslint-disable-next-line no-unused-vars
  onRowClick?: (item: { [key: string]: any }) => void;
  /** When true, row clicks do not open the default details modal. */
  disableRowDetails?: boolean;
  loading?: boolean;
  /**
   * Fields whose headers offer sorting. Sorting is opt-in: a paginated list can
   * only sort correctly on the server, so a page that has not wired
   * `onSortChange` shows no sort control rather than one that reorders the
   * current page in isolation.
   */
  sortableFields?: string[];
  /** Current server-side sort, when the page drives it. */
  sort?: TableSort;
  /** Receives the next sort, or null when sorting is cleared. */
  // eslint-disable-next-line no-unused-vars
  onSortChange?: (next: TableSort) => void;
}

type SortDirection = 'asc' | 'desc' | null;

export type TableSort = { field: string; direction: 'asc' | 'desc' } | null;

export const Table: React.FC<ITable> = ({
  headers,
  items,
  fieldsToShow,
  columns,
  currentPage,
  totalPages,
  currencies,
  detailsModalTitle,
  setCurrentPage,
  actions,
  actionsHeader = 'Actions',
  renderDetailsModal,
  renderCell,
  onRowClick,
  disableRowDetails,
  loading = false,
  sortableFields,
  sort,
  onSortChange,
}) => {
  const [activeItem, setActiveItem] = useState<{ [key: string]: any } | null>(null);
  const [localSortField, setLocalSortField] = useState<string | null>(null);
  const [localSortDirection, setLocalSortDirection] = useState<SortDirection>(null);
  const [revealRows, setRevealRows] = useState(false);
  const wasShowingLoadingRef = useRef(false);

  const { isDark } = useSelector(({ isDark }) => isDark);
  const hasContent = items.length > 0;
  const showLoading = useDeferredLoading(loading, hasContent);
  const showStale = showLoading && hasContent;

  useEffect(() => {
    if (wasShowingLoadingRef.current && !showLoading && hasContent) {
      setRevealRows(true);
      const timer = window.setTimeout(() => setRevealRows(false), 450);
      wasShowingLoadingRef.current = false;
      return () => window.clearTimeout(timer);
    }

    if (showLoading) {
      wasShowingLoadingRef.current = true;
    }

    return undefined;
  }, [showLoading, hasContent]);

  // Server-driven when the page passes onSortChange; otherwise the local state
  // below sorts the rows it was given.
  const isControlled = Boolean(onSortChange);
  const sortField = isControlled ? (sort?.field ?? null) : localSortField;
  const sortDirection: SortDirection = isControlled
    ? (sort?.direction ?? null)
    : localSortDirection;

  const canSort = (field: string) => {
    if (sortableFields) return sortableFields.includes(field);
    if (isControlled) return true;
    // Local sorting only ever sees the rows currently on screen, so it is
    // offered only when there is nothing off-screen to get wrong.
    return (totalPages ?? 1) <= 1;
  };

  const handleSort = (field: string) => {
    if (!canSort(field)) return;

    // asc -> desc -> unsorted
    const next: TableSort =
      sortField !== field
        ? { field, direction: 'asc' }
        : sortDirection === 'asc'
          ? { field, direction: 'desc' }
          : null;

    if (isControlled) {
      onSortChange?.(next);
      return;
    }

    setLocalSortField(next?.field ?? null);
    setLocalSortDirection(next?.direction ?? null);
  };

  const sortedItems = useMemo(() => {
    // The server already returned the rows in order.
    if (isControlled) return items;
    if (!sortField || !sortDirection) return items;
    if (!fieldsToShow.includes(sortField)) return items;

    const direction = sortDirection === 'asc' ? 1 : -1;

    // Numeric strings ("9" vs "10") and ISO dates both compare wrongly under
    // localeCompare, so each is detected before falling back to text.
    const rank = (value: any) => {
      if (value == null || value === '' || value === '—') return null;
      if (value instanceof Date) return value.getTime();
      if (typeof value === 'number') return value;
      if (typeof value === 'boolean') return value ? 1 : 0;

      const text = String(value).trim();
      if (text !== '' && !Number.isNaN(Number(text))) return Number(text);

      const time = Date.parse(text);
      if (!Number.isNaN(time) && /\d{4}-\d{2}-\d{2}/.test(text)) return time;

      return null;
    };

    return [...items].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      // Blanks sort last in both directions rather than clumping at one end.
      const aEmpty = aValue == null || aValue === '' || aValue === '—';
      const bEmpty = bValue == null || bValue === '' || bValue === '—';
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;

      const aRank = rank(aValue);
      const bRank = rank(bValue);
      if (aRank != null && bRank != null) return (aRank - bRank) * direction;

      return String(aValue).localeCompare(String(bValue)) * direction;
    });
  }, [items, sortField, sortDirection, fieldsToShow, isControlled]);

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return (
        <SortIcon>
          <CaretUpIcon />
          <CaretDownIcon />
        </SortIcon>
      );
    }
    return sortDirection === 'asc' ? (
      <SortIcon>
        <CaretUpIcon />
      </SortIcon>
    ) : (
      <SortIcon>
        <CaretDownIcon />
      </SortIcon>
    );
  };

  return (
    <>
      <StyledTableScroll>
        {/* The actions column is sized to its buttons rather than given a
            share of the row: a fixed set of icon buttons has a real minimum
            width, and 1fr let the last one get clipped. */}
        <StyledTable $columns={actions ? `${columns} max-content` : columns} $isDark={isDark}>
          <div className="header">
            {headers.map((header, index) => (
              <StyledHeaderCell
                key={index}
                onClick={() => handleSort(fieldsToShow[index])}
                $isSortable={canSort(fieldsToShow[index])}
              >
                <span>{header}</span>
                {canSort(fieldsToShow[index]) ? getSortIcon(fieldsToShow[index]) : null}
              </StyledHeaderCell>
            ))}
            {actions && (
              <StyledHeaderCell $isSortable={false}>
                <span>{actionsHeader}</span>
              </StyledHeaderCell>
            )}
          </div>
          <TableBodyWrap
            $dimmed={showStale}
            $reveal={revealRows}
            aria-busy={showLoading}
          >
            {sortedItems.map((item, rowIndex) => (
                <div
                  className="table-row"
                  key={getRowKey(item, rowIndex)}
                  onClick={() => {
                    if (showLoading) return;
                    if (onRowClick) {
                      onRowClick(item);
                      return;
                    }
                    if (disableRowDetails) return;
                    setActiveItem(item);
                  }}
                  style={
                    onRowClick || !disableRowDetails
                      ? { cursor: showLoading ? 'default' : 'pointer' }
                      : undefined
                  }
                >
                  {fieldsToShow.map((field, colIndex) => {
                    const custom = renderCell?.(item, field);
                    return (
                      <div className="cell" key={colIndex} data-label={headers[colIndex]}>
                        {custom != null ? custom : <ListItemRenderer item={item} field={field} />}
                      </div>
                    );
                  })}
                  {actions && (
                    <div
                      className="cell actions-cell"
                      data-label={actionsHeader}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {actions(item)}
                    </div>
                  )}
                </div>
              ))}
          </TableBodyWrap>
        </StyledTable>
      </StyledTableScroll>
      {totalPages > 1 && (
        <Pagination currentPage={currentPage} setCurrentPage={setCurrentPage} nPages={totalPages} />
      )}
      {activeItem &&
        (renderDetailsModal ? (
          renderDetailsModal(activeItem, () => setActiveItem(null))
        ) : (
          <ItemDetailsModal
            item={activeItem}
            onClose={() => setActiveItem(null)}
            currencies={currencies}
            title={detailsModalTitle}
          />
        ))}
    </>
  );
};
